import React from 'react';
import styled from 'styled-components';

const StyledTypography = styled.p`
  font-family: ${({ theme, $variant }) => {
    if ($variant === 'mono') return theme.fontFamily.mono;
    return theme.fontFamily.primary;
  }};
  
  ${({ theme, $variant }) => {
    const styles = theme.typography[$variant] || theme.typography.body1;
    return `
      font-size: ${styles.fontSize};
      font-weight: ${styles.fontWeight};
      line-height: ${styles.lineHeight};
      letter-spacing: ${styles.letterSpacing || '0'};
    `;
  }}
  
  color: ${({ theme, $color }) => {
    if (!$color) return theme.colors.text.primary;
    if (typeof $color === 'string' && $color.startsWith('#')) return $color;
    return theme.colors.text[$color] || theme.colors[$color] || $color;
  }};
  
  text-transform: ${({ $textTransform }) => $textTransform || 'none'};
  
  margin: ${({ $margin, $noMargin }) => {
    if ($noMargin) return '0';
    if ($margin) return $margin;
    return '0 0 1rem 0';
  }};
  
  padding: ${({ $padding }) => $padding || '0'};
  
  text-align: ${({ $align }) => $align || 'left'};
  
  ${({ $gutterBottom }) => $gutterBottom && `
    margin-bottom: 1rem;
  `}
  
  ${({ $gutterTop }) => $gutterTop && `
    margin-top: 1rem;
  `}
  
  ${({ theme, $variant }) => {
    if ($variant === 'overline') {
      return `
        text-transform: uppercase;
        letter-spacing: ${theme.letterSpacing.widest};
        opacity: 0.7;
      `;
    }
    if ($variant === 'button') {
      return `
        text-transform: uppercase;
        letter-spacing: ${theme.letterSpacing.wide};
      `;
    }
    return '';
  }}
  
  ${({ $truncate }) => $truncate && `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const Typography = React.forwardRef(
  ({ variant = 'body1', component, children, ...props }, ref) => {
    const Component = component || (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(variant) ? variant : 'p');

    // Filter out props that should not go to DOM elements
    const { gutterBottom, gutterTop, noMargin, margin, padding, truncate, textTransform, ...domProps } = props;

    return (
      <StyledTypography
        as={Component}
        $variant={variant}
        $gutterBottom={gutterBottom}
        $gutterTop={gutterTop}
        $noMargin={noMargin}
        $margin={margin}
        $padding={padding}
        $truncate={truncate}
        $textTransform={textTransform}
        ref={ref}
        {...domProps}
      >
        {children}
      </StyledTypography>
    );
  }
);

Typography.displayName = 'Typography';

export default Typography;